import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { map, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { TARGET_SITE_PROGRAM } from '@app/shared/constants';
import { TokenStorageService } from '@app/shared/services/token-storage.service';
import { AuthService } from '@app/api/api-services/auth.service';
import { ProgramType } from '@app/shared/enums';
import { RouteHelperService } from '@app/shared/services/route-helper.service';
import { RedirectService } from '@app/shared/services/redirect.service';

@Injectable()
export class TargetSiteProgramGuard {
  constructor(
    private readonly routeHelper: RouteHelperService,
    private readonly tokenStorage: TokenStorageService,
    private readonly authService: AuthService,
    private readonly redirectService: RedirectService
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean | Observable<boolean> {
    const userInfo = this.tokenStorage.userInfo;

    const targetSiteId = +route.queryParams[TARGET_SITE_PROGRAM.targetSiteId];
    let targetProgramId = +route.queryParams[TARGET_SITE_PROGRAM.targetProgramId];

    if (!targetSiteId && !targetProgramId) {
      return this.validateProgramType(route);
    }

    if (!userInfo) {
      return false;
    }

    if (!targetProgramId && userInfo.CurrentSiteId === targetSiteId) {
      targetProgramId = userInfo.CurrentProgramId;
    }

    if (userInfo.CurrentSiteId === targetSiteId && userInfo.CurrentProgramId === targetProgramId) {
      return this.validateProgramType(route);
    }

    return this.authService
      .changeCurrent(targetSiteId, targetProgramId, false)
      .pipe(map(() => this.validateProgramType(route)))
      .pipe(
        catchError(() => {
          this.routeHelper.forbidden();
          return of(false);
        })
      );
  }

  public canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    return this.canActivate(childRoute, state);
  }

  private validateProgramType(route: ActivatedRouteSnapshot): boolean {
    const programType = route.data.programType as ProgramType;

    if (!programType) {
      return true;
    }

    if (!this.tokenStorage.userInfo) {
      return false;
    }

    if (this.tokenStorage.userInfo?.CurrentProgram.Type === programType) {
      return true;
    }

    this.redirectService.redirectToDefault();

    return false;
  }
}
